import { Check, Package, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface Pack {
  key: string;
  name: string;
  description: string | null;
  definition: Record<string, unknown[]>;
}

interface Installed {
  pack_key: string;
  installed_at: string;
  summary: Record<string, number>;
}

const SECTION_LABELS: Record<string, string> = {
  pipeline_stages: "pipeline stages",
  tags: "tags",
  checklists: "checklists",
  message_templates: "message templates",
  campaign_templates: "campaign drafts",
  workflows: "workflows",
};

const describe = (definition: Record<string, unknown[]>) =>
  Object.entries(SECTION_LABELS)
    .filter(([key]) => Array.isArray(definition[key]) && definition[key].length)
    .map(([key, label]) => `${definition[key].length} ${label}`)
    .join(" · ");

/**
 * Industry starter packs.
 *
 * A pack rewrites the default pipeline's stages and adds tags, checklists,
 * message templates, campaign drafts and workflows. That is a lot to do on one
 * click, so the confirm dialog states exactly what will change, particularly
 * that stages are REPLACED, which is the only destructive part.
 *
 * Workflows arrive switched OFF. The server does that; this screen says so,
 * because "installed a template" should never mean "started texting customers".
 */
export const TemplatePacksSection = () => {
  const notify = useNotify();
  const workspaceId = getActiveWorkspaceId();
  const [confirming, setConfirming] = useState<Pack | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: packs } = useQuery<Pack[]>({
    queryKey: ["template-packs"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("industry_template_packs")
        .select("key, name, description, definition")
        .order("position");
      if (error) throw new Error(error.message);
      return (data ?? []) as Pack[];
    },
  });

  const { data: installed, refetch } = useQuery<Installed[]>({
    queryKey: ["installed-packs", workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      // Filter by the ACTIVE workspace explicitly. RLS admits every workspace
      // the user belongs to, so a two-tenant user saw "Installed" here for a
      // pack they had installed in the OTHER workspace, same class of bug as
      // the `sales` people-picker. RLS bounds what is visible; it does not
      // pick which workspace you are currently looking at.
      const { data, error } = await getSupabaseClient()
        .from("installed_template_packs")
        .select("pack_key, installed_at, summary")
        .eq("workspace_id", workspaceId!);
      if (error) throw new Error(error.message);
      return (data ?? []) as Installed[];
    },
  });

  const installedKeys = new Set((installed ?? []).map((row) => row.pack_key));

  const install = async (pack: Pack) => {
    setBusy(true);
    const { data, error } = await getSupabaseClient().rpc(
      "install_template_pack",
      { p_workspace_id: workspaceId, p_pack_key: pack.key },
    );
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    const counts = (data ?? {}) as Record<string, number>;
    const added = Object.entries(counts)
      .filter(([, n]) => n > 0)
      .map(([key, n]) => `${n} ${SECTION_LABELS[key] ?? key}`)
      .join(", ");
    notify(
      added
        ? `Installed ${pack.name}: ${added}`
        : `${pack.name} was already installed`,
    );
    setConfirming(null);
    refetch();
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <p className="text-[13px] text-muted-foreground">
        Set up Gravun for your trade in one step. A pack renames your pipeline
        stages and adds tags, checklists, message templates, campaign drafts and
        automations you can edit afterwards.
      </p>

      <ul className="grid gap-2 md:grid-cols-2 [&>*]:min-w-0">
        {(packs ?? []).map((pack) => {
          const isInstalled = installedKeys.has(pack.key);
          return (
            <li
              key={pack.key}
              className="flex min-w-0 flex-col gap-2 rounded-md border border-border px-3 py-2.5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-[13.5px] font-medium text-foreground">
                    <Package className="size-3.5 text-muted-foreground" />
                    {pack.name}
                    {isInstalled ? (
                      <span className="flex items-center gap-0.5 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 text-[11px] font-medium text-emerald-800">
                        <Check className="size-2.5" />
                        Installed
                      </span>
                    ) : null}
                  </p>
                  <p className="text-[12px] text-muted-foreground">
                    {pack.description}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={isInstalled ? "ghost" : "outline"}
                  className="h-8 shrink-0"
                  onClick={() => setConfirming(pack)}
                >
                  {isInstalled ? "Reinstall" : "Install"}
                </Button>
              </div>
              <p className="text-[11.5px] text-muted-foreground">
                {describe(pack.definition)}
              </p>
            </li>
          );
        })}
      </ul>

      <Dialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Install {confirming?.name}?</DialogTitle>
          </DialogHeader>
          {confirming ? (
            <div className="flex flex-col gap-3">
              <p className="text-[13px] text-muted-foreground">
                This adds {describe(confirming.definition)}.
              </p>

              <div
                className={cn(
                  "flex items-start gap-1.5 rounded-md border border-amber-200",
                  "bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900",
                )}
              >
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  Your pipeline stages are <strong>replaced</strong> with this
                  pack's. Existing deals keep their stage and appear in the
                  first column if their old stage is gone. Everything else is
                  added alongside what you already have.
                </span>
              </div>

              <p className="text-[12px] text-muted-foreground">
                Automations are installed switched off, so nothing is sent to a
                customer until you turn them on.
              </p>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirming(null)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => install(confirming)}
                >
                  {busy ? "Installing…" : `Install ${confirming.name}`}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
